/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast.  Keeping your custom
* Javascript in this fill will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/

(function () {
  const MAX_DONATION = 10000;
  const MIN_DONATION = 1;
  const ADD_TIMEOUT = 8000;
  const NUDGE_DURATION = 5000;

  const selectors = {
    donationInput: '.custom-donation-input',
    quickAddHolder: '[data-quick-add-holder]',
    addButton: '[data-quick-add-btn][data-add-to-cart]',
    buttonOuter: '.btn__outer',
    cartForm: '[data-cart-form]',
    cartScope: 'cart-drawer, [data-cart-page]',
    checkoutButton: '[name="checkout"]',
    nudgeMessage: '[data-donation-nudge]',
  };

  const classes = {
    nudge: 'donation-nudge',
    nudgeMessage: 'donation-nudge__message',
    checkoutBusy: 'donation-checkout-busy',
  };

  const strings = {
    addFailed: 'We could not add your donation — press Add to try again.',
    needAmount: 'Enter a donation amount first.',
  };

  // Set while we re-trigger checkout ourselves, so we do not intercept our own submit
  let bypassInterception = false;
  // Guards against a second interception while an add is already in flight
  let addInFlight = false;

  // The donation is added as one unit of the $1 donation product per dollar, so
  // the amount rides on the quantity. Shopify merges repeat adds of the same
  // variant on its own, which is why adding twice tops up a single line.
  function syncQuantityToForm(input) {
    const formId = input.dataset.formId;
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;
    const rawValue = input.value.trim();
    const amount = parseInt(rawValue, 10);
    const quantity = isNaN(amount) || amount < MIN_DONATION ? MIN_DONATION : Math.min(amount, MAX_DONATION);
    const existing = form.querySelector('input[data-donation-quantity]');
    if (existing) existing.remove();
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'quantity';
    hidden.value = quantity;
    hidden.dataset.donationQuantity = 'true';
    form.appendChild(hidden);
  }

  function handleInput(e) {
    const input = e.target;
    if (!input.classList || !input.classList.contains('custom-donation-input')) return;
    let value = input.value;
    value = value.replace(/\D/g, '');
    if (value !== '') {
      let num = parseInt(value, 10);
      if (num > MAX_DONATION) num = MAX_DONATION;
      value = num.toString();
    }
    input.value = value;

    // Only an amount the shopper actually typed counts as a pending donation —
    // handleBlur also writes a value into a field that was never typed in.
    if (value === '') {
      delete input.dataset.donationTouched;
    } else {
      input.dataset.donationTouched = 'true';
    }

    clearNudge(input);
    syncQuantityToForm(input);
  }

  function handleBlur(e) {
    const input = e.target;
    if (!input.classList || !input.classList.contains('custom-donation-input')) return;

    // Leave an empty field empty. Filling in the minimum here used to turn
    // "clicked Add without typing anything" into a real $1 donation, because
    // clicking the button blurs the field before the click is handled.
    if (input.value !== '' && parseInt(input.value, 10) < MIN_DONATION) {
      input.value = String(MIN_DONATION);
    }

    syncQuantityToForm(input);
  }

  function initDonationInputs() {
    document.querySelectorAll(selectors.donationInput).forEach(function (input) {
      if (input.dataset.initialized === 'true') return;
      input.dataset.initialized = 'true';
      if (input.value !== '') syncQuantityToForm(input);
    });
  }

  /**
   * Pending donation helpers
   */

  function getAddButton(input) {
    const holder = input.closest(selectors.quickAddHolder);
    return holder ? holder.querySelector(selectors.addButton) : null;
  }

  function isDonationPending(input) {
    if (input.dataset.donationTouched !== 'true') return false;
    const amount = parseInt(input.value, 10);
    if (isNaN(amount) || amount < MIN_DONATION) return false;
    const button = getAddButton(input);
    // No usable button (sold out, or quick add hidden) — never block checkout
    return Boolean(button) && !button.disabled;
  }

  function findPendingDonation(scope) {
    const root = scope || document;
    return Array.from(root.querySelectorAll(selectors.donationInput)).find(isDonationPending) || null;
  }

  function resetDonationInput(input) {
    input.value = '';
    delete input.dataset.donationTouched;
    clearNudge(input);
    syncQuantityToForm(input);
  }

  /**
   * Checkout interception
   */

  function setCheckoutBusy(button, busy) {
    if (!button) return;
    button.classList.toggle(classes.checkoutBusy, busy);
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.disabled = busy;
  }

  function handleCheckoutSubmit(e) {
    const form = e.target;
    if (!form || !form.matches || !form.matches(selectors.cartForm)) return;

    if (bypassInterception) return;

    if (addInFlight) {
      e.preventDefault();
      return;
    }

    const scope = form.closest(selectors.cartScope) || document;
    const input = findPendingDonation(scope);
    if (!input) return;

    e.preventDefault();
    addDonationThenCheckout(input, form);
  }

  // The theme only updates the cart in place for the drawer — with the cart page it
  // reloads the page after an add, which would drop the shopper out of checkout.
  function hasDrawerFlow() {
    return window.theme?.settings?.cartType === 'drawer' && Boolean(document.querySelector('cart-drawer'));
  }

  function addDonationThenCheckout(input, form) {
    const button = getAddButton(input);
    if (!button) return;

    addInFlight = true;
    setCheckoutBusy(form.querySelector(selectors.checkoutButton), true);

    performDonationAdd(input, button)
      .then(() => {
        addInFlight = false;
        resumeCheckout(form);
      })
      .catch(() => {
        addInFlight = false;
        setCheckoutBusy(form.querySelector(selectors.checkoutButton), false);
        nudge(input, strings.addFailed);
      });
  }

  function readAmount(input) {
    const amount = parseInt(input.value.trim(), 10);
    if (isNaN(amount) || amount < MIN_DONATION) return null;
    return Math.min(amount, MAX_DONATION);
  }

  function performDonationAdd(input, button) {
    return new Promise((resolve, reject) => {
      let settled = false;
      let timer = null;

      const cleanup = () => {
        clearTimeout(timer);
        document.removeEventListener('theme:product:added', onAdded);
        document.removeEventListener('theme:product:add-error', onError);
      };

      const onAdded = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };

      const onError = () => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('Donation add failed'));
      };

      timer = setTimeout(onError, ADD_TIMEOUT);

      if (hasDrawerFlow()) {
        document.addEventListener('theme:product:added', onAdded);
        document.addEventListener('theme:product:add-error', onError);

        // Same path as pressing "Add" — the theme builds the form data and updates the cart
        document.dispatchEvent(
          new CustomEvent('theme:cart:add', {
            detail: {
              button: button,
            },
          })
        );

        return;
      }

      addDonationDirect(input, button, onAdded, onError);
    });
  }

  // Cart page: add the line item ourselves and go straight to checkout
  function addDonationDirect(input, button, onAdded, onError) {
    const upsellForm = button.closest('form');
    if (!upsellForm) {
      onError();
      return;
    }

    button.classList.add('is-loading');
    button.disabled = true;

    fetch(window.theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
      body: new FormData(upsellForm),
    })
      .then((response) => response.json())
      .then((response) => {
        button.classList.remove('is-loading');
        button.disabled = false;

        if (response.status) {
          onError();
          return;
        }

        resetDonationInput(input);
        onAdded();
      })
      .catch(() => {
        button.classList.remove('is-loading');
        button.disabled = false;
        onError();
      });
  }

  /**
   * Pressing "Add" goes through us only to refuse an empty amount. Anything
   * valid is handed straight back to the theme's own add, which keeps the drawer
   * and its loading states intact.
   */
  function handleDonationAddClick(e) {
    const target = e.target;
    if (!target || !target.closest) return;

    const button = target.closest(selectors.addButton);
    if (!button || button.disabled) return;

    const holder = button.closest(selectors.quickAddHolder);
    const input = holder ? holder.querySelector(selectors.donationInput) : null;
    if (!input) return;

    e.preventDefault();
    e.stopPropagation();

    // Nothing typed, so there is no donation to add. `donationTouched` is set by
    // handleInput and cleared whenever the field goes empty, so it distinguishes
    // a real amount from a field the shopper never filled in.
    if (input.dataset.donationTouched !== 'true' || readAmount(input) === null) {
      nudge(input, strings.needAmount);
      input.focus();
      return;
    }

    if (addInFlight) return;
    addInFlight = true;

    performDonationAdd(input, button)
      .catch(() => nudge(input, strings.addFailed))
      .then(() => {
        addInFlight = false;
      });
  }

  function resumeCheckout(form) {
    // Re-read the button in case the cart markup was re-rendered by the add
    const currentForm = document.querySelector(selectors.cartForm) || form;
    const checkoutButton = currentForm.querySelector(selectors.checkoutButton);

    setCheckoutBusy(checkoutButton, false);

    // Click the real button so theme checks (e.g. terms acceptance) still run.
    // The submit event fires synchronously, so the flag can be cleared right after.
    bypassInterception = true;
    if (checkoutButton) {
      checkoutButton.click();
    } else {
      currentForm.submit();
    }
    bypassInterception = false;
  }

  /**
   * Fallback nudge — highlight "Add" when the donation could not be added for them
   */

  function nudge(input, message) {
    const holder = input.closest(selectors.quickAddHolder);
    const outer = holder ? holder.querySelector(selectors.buttonOuter) : null;
    if (!holder || !outer) return;

    outer.classList.add(classes.nudge);

    let note = holder.querySelector(selectors.nudgeMessage);
    if (!note) {
      note = document.createElement('p');
      note.className = classes.nudgeMessage;
      note.setAttribute('data-donation-nudge', '');
      note.setAttribute('role', 'status');
      holder.appendChild(note);
    }
    note.textContent = message;

    outer.scrollIntoView({block: 'nearest', behavior: 'smooth'});

    clearTimeout(holder.donationNudgeTimer);
    holder.donationNudgeTimer = setTimeout(() => clearNudge(input), NUDGE_DURATION);
  }

  function clearNudge(input) {
    const holder = input.closest(selectors.quickAddHolder);
    if (!holder) return;

    clearTimeout(holder.donationNudgeTimer);
    holder.querySelector(selectors.buttonOuter)?.classList.remove(classes.nudge);
    holder.querySelector(selectors.nudgeMessage)?.remove();
  }

  /**
   * Clear the amount once it is in the cart, so it is not counted as pending again
   */
  function handleProductAdd(e) {
    const button = e.detail && e.detail.button;
    if (!button || !button.closest) return;
    const holder = button.closest(selectors.quickAddHolder);
    const input = holder ? holder.querySelector(selectors.donationInput) : null;
    if (!input) return;
    resetDonationInput(input);
  }

  document.addEventListener('click', handleDonationAddClick, true);
  document.addEventListener('input', handleInput, true);
  document.addEventListener('blur', handleBlur, true);
  document.addEventListener('submit', handleCheckoutSubmit, true);
  document.addEventListener('theme:product:add', handleProductAdd);
  document.addEventListener('theme:cart:change', initDonationInputs);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDonationInputs);
  } else {
    initDonationInputs();
  }

  window.initDonationInputs = initDonationInputs;
})();